import {Component} from '@angular/core';
import { Action } from '../../../../domain/infra/remote';
import { FrontOtpBusinessService } from '../../../../services/business/otpBusiness.service';
import { FrontOtpHttpService } from '../../../../services/http/otpHttp.service';
import { IziviaLoggerService } from '../../../../services/common/iziviaLogger.service';
import { OTPSessionStatus } from '../../../../domain/otp/otp';
import { PricingContextDto } from '../../../../domain/map/chargeAreaDetailsMarker';
import { RemoteResponse } from '../../../../domain/infra/remote';
import { RemoteStatus } from '../../../../domain/infra/remote';
import { StationDto } from '../../../../domain/infra/station/station';
import { UserAction } from '../../../../domain/infra/userAction';
import {ActivatedRoute} from '@angular/router';
import {NavController, ViewDidEnter, ViewWillEnter, ViewWillLeave} from '@ionic/angular';
import { IziviaToastService } from '../../../../services/common/iziviaToast.service';
import { NewChargeContext } from '../../../../services/business/newChargeContext.service';
import { NewChargeStepType } from '../../../../domain/headerStep';
import { RemoteInfoMedia } from '../../../../domain/chargeRecord/remoteInfoChargeRecord';
import {interval, Observable, of, Subscription} from 'rxjs';
import {filter, first, map, startWith, switchMap, take} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'new-charge-summary-view',
    templateUrl: './summary.view.html',
    styleUrls: ['./summary.view.scss']
})
export class NewChargeSummaryView  implements ViewWillEnter, ViewDidEnter, ViewWillLeave {
    // dirty hack to enable type-safety to the view
    Action = Action;

    newChargeContext: NewChargeContext;
    step: NewChargeStepType = NewChargeStepType.SUMMARY;
    station: StationDto;
    canRemoteStart: boolean;
    remoteStart: boolean;
    remoteRequest: RemoteInfoMedia;
    pricingContextObs: Observable<PricingContextDto>;
    otpSessionReadyToCharge: boolean;

    private pollingSubscription: Subscription;

    constructor(private readonly route: ActivatedRoute,
        private readonly navController: NavController,
        private readonly loggerService: IziviaLoggerService,
        private readonly otpHttpService: FrontOtpHttpService,
        private readonly otpBusinessService: FrontOtpBusinessService,
        private readonly toastService: IziviaToastService,
        private readonly translateService: TranslateService
    ) {}

    ionViewWillEnter() {
        this.station = this.route.snapshot.data.station;
        this.newChargeContext = this.route.snapshot.data.newChargeContext;
        this.pricingContextObs = of(this.station.pricingContext);

        this.loggerService.debug('new charge summary: context found', this.newChargeContext);

        const language = localStorage.getItem('otpSession.language');
        if (language !== null && language !== this.translateService.currentLang) {
            this.translateService.use(language);
        }

        this.canRemoteStart = this.newChargeContext.chargePoint.currentUserActions.includes(UserAction.REMOTE_START)
                && (!this.newChargeContext.otpSession || this.doesOtpSessionAllowRemoteStart());

        localStorage.removeItem(this.route.snapshot.data.newChargeContext.id);
        this.pollOtpSessionUntilReadyToCharge(this.newChargeContext.otpSession._id);
    }

    ionViewDidEnter() {
        // force event refresh in order to show a notification badge in the footer tabs
    }

    ionViewWillLeave() {
        this.pollingSubscription?.unsubscribe();
    }

    private pollOtpSessionUntilReadyToCharge(otpSessionId: string) {
        this.pollingSubscription = interval(5 * 1000) // Every 5 seconds
            .pipe(
                startWith(0),
                take(12), // Times out after one minute
                switchMap(() => this.otpHttpService.getOtpSession(otpSessionId)),
                filter(otpSession => !!otpSession),
                filter(otpSession => !this.otpBusinessService.canEvolve(otpSession)),
                first(),
                map(otpSession => {
                    if (otpSession.status === OTPSessionStatus.ENDED) {
                        return otpSession;
                    }

                    throw new Error(otpSession.status);
                })
            )
            .subscribe({
                next: endedOtpSession => this.otpSessionReadyToCharge = true,
                error: error => {
                    this.loggerService.warning(`[pollOtpSessionUntilEndedAndGoToNextSlide] Otp session polling stopped.`,
                        otpSessionId, error);
                    this.toastService.showError('otp.error.default');
                    this.navController.navigateRoot('/events', {replaceUrl: true});
                }
            });
    }

    showRemoteStart() {
        this.remoteStart = true;

        this.remoteRequest = new RemoteInfoMedia(
            this.newChargeContext.otpSession.stationRef,
            this.newChargeContext.otpSession.chargePointRef,
            this.newChargeContext.otpSession.plugRef,
            this.newChargeContext.otpSession.otpCode
        );
    }

    handleRemoteResponse(remoteResponse: RemoteResponse) {
        if (remoteResponse.status === RemoteStatus.DONE) {
            if (remoteResponse.chargeRef) {
                const url = `/events/details/charge/${remoteResponse.chargeRef}/otp/${this.newChargeContext.otpSession.otpCode}`;
                this.navController.navigateRoot(url, {replaceUrl : true, animationDirection : 'forward'});
            } else {
                this.loggerService.error('[handleRemoteResponse]: remoteResponse.chargeRef === null', remoteResponse);
                this.navController.navigateRoot('/home',
                    {replaceUrl : true, animationDirection : 'forward'});
            }
        } else if (remoteResponse.status === RemoteStatus.TIMEOUT) {
            this.loggerService.debug('[handleRemoteResponse] TIMOUT', remoteResponse);
            this.navController.navigateForward([`/app/new-charge/station/${this.station.id}/retry`], {
                queryParams: this.newChargeContext.toQueryParam()
            });
        } else {
            this.remoteStart = false;
            this.remoteRequest = null;
            this.navController.navigateForward([`/app/new-charge/station/${this.station.id}/retry`], {
                queryParams: this.newChargeContext.toQueryParam()
            });
        }
    }

    goToHome() {
        this.navController.navigateRoot(['/home'], {
            replaceUrl: true
        });
    }

    private doesOtpSessionAllowRemoteStart(): boolean {
        const otpSession = this.newChargeContext.otpSession;

        return !!otpSession
            && !!otpSession.stationRef
            && !!otpSession.chargePointRef
            && !!otpSession.plugRef
            && !!otpSession.otpCode;
    }
}
